import React from "react";
import { Field, FormikValues, useFormikContext } from "formik";
import Select, {
    components,
    DropdownIndicatorProps,
    MultiValue,
    SingleValue,
} from "react-select";
import { isArray } from "lodash";

import { bemClassName } from "../../utils/bemClassName";
import { joinClassNames } from "../../utils/joinClassNames";
import { useSearchFormContext } from "../../context/SearchFormContext";
import FacetsApplyButton from "./FacetsApplyButton";
import FacetsClearButton from "./FacetsClearButton";
import { ReactComponent as IconChevronDown } from "../../assets/icons/chevron-down.svg";
import { ReactComponent as IconTick } from "../../assets/icons/tick.svg";
import DateRangeField from "./DateRangeField";

import {
    SearchFieldCheckbox,
    SearchFieldMultiSelect,
    SearchFieldOption,
    SearchFieldRange,
    SearchFieldSelect,
} from "./SearchForm";

import "./SelectField.css";
import "./DateRangeField.css";
import styles from "./Facets.module.css";
import resolveFacetItems from "./resolveFacetsItems";
import { useSearchResults } from "../../context/DataContext";
import {
    getMultiSelectFacetColumnClass,
    getSelectFacetColumnClass,
    getSelectFacetPlaceholder,
    shouldHideCheckboxGroupLegend,
} from "../../search";

const hasPopulatedFacets = () => {
    const { values } = useFormikContext() as FormikValues;
    const nonFacetFields = ["collection", "query"];
    const facetFields = Object.keys(values).filter(
        (fieldName) => !nonFacetFields.includes(fieldName)
    );
    const hasPopulatedValues = facetFields.find((fieldName) => {
        const value = values[fieldName];
        if (value.length === 0) {
            return false;
        }
        const isPopulated = (isArray(value) && value.length) || !!value;
        return isPopulated;
    });
    return !!hasPopulatedValues;
};

const DropdownIndicator = (
    props: DropdownIndicatorProps<SearchFieldOption>
) => {
    return (
        <components.DropdownIndicator {...props}>
            <IconChevronDown
                className={bemClassName(
                    "react-select",
                    "dropdown-indicator-icon"
                )}
            />
        </components.DropdownIndicator>
    );
};

type CheckboxGroupOptionProps = SearchFieldOption & {
    name: string;
};

export const SelectFacet = ({
    id,
    label,
    name,
    options,
}: SearchFieldSelect) => {
    const { initialValues, setFieldValue, values } =
        useFormikContext() as FormikValues;
    const { config } = useSearchResults();

    const initialSelectedValue = initialValues[name];
    const initialSelectedIndex = options.findIndex(
        (option) => option.value === initialSelectedValue
    );
    const defaultValue =
        initialSelectedIndex === -1 ? null : options[initialSelectedIndex];

    const selectedValue = values[name];
    const selectedIndex = options.findIndex(
        (option) => option.value === selectedValue
    );
    const value = selectedIndex === -1 ? null : options[selectedIndex];

    const handleSelectChange = (
        newValue:
            | SingleValue<SearchFieldOption>
            | MultiValue<SearchFieldOption>,
        name: string
    ) => {
        if (isArray(newValue)) {
            setFieldValue(
                name,
                newValue.map((item) => item.value)
            );
        } else if (!newValue || !("value" in newValue)) {
            setFieldValue(name, "");
        } else {
            setFieldValue(name, newValue.value);
        }
    };

    const columnClassName = getSelectFacetColumnClass(config.collection, label);

    return (
        <div className={columnClassName}>
            <label
                htmlFor={id}
                className={joinClassNames([
                    bemClassName("find-a-panel", "search-title"),
                    styles.selectLabel,
                ])}
            >
                {label}
            </label>
            <Select
                id={id}
                options={options}
                defaultValue={defaultValue}
                value={value}
                placeholder={getSelectFacetPlaceholder(config.collection, label)}
                classNamePrefix="react-select"
                unstyled
                onChange={(selectedOption) =>
                    handleSelectChange(selectedOption, name)
                }
                components={{
                    DropdownIndicator,
                }}
            />
        </div>
    );
};

export const MultiSelectFacet = ({
    id,
    label,
    name,
    options,
}: SearchFieldMultiSelect) => {
    const { config } = useSearchResults();
    const { initialValues, setFieldValue, values } =
        useFormikContext() as FormikValues;
    const initialSelectedValue = initialValues[name];
    const initialSelectedIndex = options.findIndex(
        (option) => option.value === initialSelectedValue
    );
    const defaultValue =
        initialSelectedIndex === -1 ? null : [options[initialSelectedIndex]];

    const selectedValue = values[name];
    const value = selectedValue
        ? options.filter((option) => selectedValue.includes(option.value))
        : [];

    const columnClassName = getMultiSelectFacetColumnClass(
        config.collection,
        label
    );

    return (
        <div className={columnClassName}>
            <label
                htmlFor={id}
                className={joinClassNames([
                    bemClassName("find-a-panel", "search-title"),
                    styles.selectLabel,
                ])}
            >
                {label}
            </label>
            <Select
                id={id}
                options={options}
                defaultValue={defaultValue}
                value={value}
                isMulti={true}
                isClearable={false}
                delimiter=", "
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                placeholder={getSelectFacetPlaceholder(config.collection, label)}
                classNamePrefix="react-select"
                unstyled
                onChange={(selectedOption) =>
                    setFieldValue(
                        name,
                        selectedOption.map((item) => item.value)
                    )
                }
                formatOptionLabel={(data, formatOptionLabelMeta) => {
                    // If is in menu list
                    if (formatOptionLabelMeta.context === "menu") {
                        const isSelected = !!value.find(
                            (option) => option.id === data.id
                        );
                        // Check if is selected
                        return (
                            <>
                                <span
                                    className={joinClassNames([
                                        styles.checkboxIconBg,
                                        "checkbox-icon-bg",
                                    ])}
                                >
                                    <IconTick
                                        className={joinClassNames([
                                            isSelected ? "is-checked" : "",
                                            styles.checkboxIcon,
                                        ])}
                                    />
                                </span>{" "}
                                {data.label}
                            </>
                        );
                    }
                    return data.label;
                }}
                components={{
                    DropdownIndicator,
                    MultiValueRemove: () => null,
                    MultiValue: (props) => {
                        if (!("data" in props)) return null;

                        // eslint-disable-next-line react/prop-types
                        const { children } = props;

                        // eslint-disable-next-line react/prop-types
                        if (props.data.id === value[value.length - 1]?.id) {
                            return <>{children}</>;
                        }
                        return <>{children},&nbsp;</>;
                    },
                }}
            />
        </div>
    );
};

const CheckboxGroupOption = ({
    id,
    label,
    name,
    value,
}: CheckboxGroupOptionProps) => {
    return (
        <div className={styles.checkboxControlGroup}>
            <Field
                type="checkbox"
                name={name}
                id={id}
                value={value}
                className={joinClassNames([
                    "visuallyhidden",
                    styles.checkboxField,
                ])}
            />
            <label htmlFor={id} className={styles.checkboxLabel}>
                <span className={styles.checkboxIconBg}>
                    <IconTick className={styles.checkboxIcon} />
                </span>
                <span
                    className={joinClassNames([
                        styles.checkboxText,
                        styles?.[id],
                    ])}
                >
                    {label.trim()}
                </span>
            </label>
        </div>
    );
};

export const CheckboxGroupFacet = ({
    label,
    name,
    options,
}: SearchFieldCheckbox) => {
    const { config } = useSearchResults();
    const getColumnWidth = (label: string) => {
        let columnWidth = 6;
        // listing-guide resources facet "Archived"
        if (label === "Archived") {
            columnWidth = 4;
            // listing-guide resources facet "Date published"
        } else if (label === "Date published") {
            columnWidth = 8;
            // ministerial permit facet "Status" || dfp
        } else if (
            name === "FUN2ge6gwydbbaipmvmk9wnkg3pr" ||
            name === "FUN2gvdke45sqknzbfs6ic975fzn"
        ) {
            columnWidth = 12;
            // EES projects and referrals
        } else if (label === "Show accredited assessments") {
            columnWidth = 12;
        }

        return columnWidth;
    };

    let customCss = ``;
    if (shouldHideCheckboxGroupLegend(config.collection)) {
        customCss = `hidden`;
    }

    return (
        <div
            className={`col-xs-12 col-lg-${getColumnWidth(
                label
            )} facet_${name}`}
        >
            <fieldset>
                <legend
                    className={joinClassNames([
                        bemClassName("find-a-panel", "search-title"),
                        styles.checkboxGroupLabel,
                        styles?.[name],
                        customCss,
                    ])}
                >
                    {label.trim()}
                </legend>
                {options.map((option) => (
                    <CheckboxGroupOption
                        key={option.id}
                        {...option}
                        name={name}
                    />
                ))}
            </fieldset>
        </div>
    );
};

export type FacetProps = {
    id: string;
};

type FacetType =
    | SearchFieldSelect
    | SearchFieldCheckbox
    | SearchFieldMultiSelect
    | Required<SearchFieldRange>;

const renderFacetField = (facet: FacetType) => {
    switch (facet.facetType) {
        case "select":
            return <SelectFacet key={`facet-key-${facet.id}`} {...facet} />;
        case "multiSelect":
            return (
                <MultiSelectFacet key={`facet-key-${facet.id}`} {...facet} />
            );
        case "checkboxGroup":
            return (
                <CheckboxGroupFacet key={`facet-key-${facet.id}`} {...facet} />
            );
        case "dateRange":
            return <DateRangeField key={`facet-key-${facet.id}`} {...facet} />;
        default:
            return null;
    }
};

const Facets = ({ id }: FacetProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const { query: _, ...facetItems } = useSearchFormContext();
    const facetFields = resolveFacetItems(facetItems) as FacetType[];
    const { facets } = useSearchResults();

    type OptionalFacetsProps = {
        id?: string;
    };
    const optionalProps: OptionalFacetsProps = {};
    if (id) {
        optionalProps.id = id;
    }

    const maybeClearFilters = hasPopulatedFacets() ? (
        <FacetsClearButton />
    ) : null;

    return facets.length > 0 ? (
        <div {...optionalProps}>
            <div className="row">
                {facetFields.map(renderFacetField)}
                <div className={joinClassNames(["col-xs-12", styles.controls])}>
                    <FacetsApplyButton />
                    {maybeClearFilters}
                </div>
            </div>
        </div>
    ) : null;
};

export default Facets;
